import { useEffect, useRef } from 'react';

import { useIsObserved } from 'common/hooks/intersection-observer';
import { useMatchesViewport } from 'common/hooks/media';
import { isClient } from 'common/utils/isClient';

const ANIMATION_THRESHOLD = 3;
const ANIMATION_DELAY = 300;
const ANIMATION_DURATION = 200;
const ANIMATION_SHAKE_OFFSET = 20;
const ANIMATION_SHAKE_CYCLES = 2;
const ANIMATION_VIEW_COUNT_KEY = 'promotedFiltersAnimationViewCount';

export type SlideAnimationOptions = {
  filters: HTMLDivElement;
  originalScroll: number;
  slideDistance: number;
  slideTimes: number;
  slideDuration: number;
};

export const startSlideAnimation = ({
  filters,
  originalScroll,
  slideDistance,
  slideTimes,
  slideDuration,
}: SlideAnimationOptions) => {
  const slideStep = (direction: number, slides: number) => {
    if (slides >= slideTimes * 2) {
      filters.scrollTo({ left: originalScroll, behavior: 'smooth' });

      return;
    }
    filters.scrollTo({ left: originalScroll + direction * slideDistance, behavior: 'smooth' });
    setTimeout(() => slideStep(-direction, slides + 1), slideDuration);
  };
  slideStep(1, 0);
};

export const usePromotedFiltersSlideAnimation = (
  listRef: React.RefObject<HTMLDivElement | null>
) => {
  const hasAnimatedRef = useRef(false);
  const [setObservedRef, isObserved] = useIsObserved();
  const {
    matches: { matchesDesktop },
  } = useMatchesViewport(false, false, false);
  useEffect(() => {
    if (listRef.current) {
      setObservedRef(listRef.current);
    }
  }, [listRef, setObservedRef]);

  useEffect(() => {
    if (!isObserved || !isClient() || hasAnimatedRef.current || matchesDesktop) return;

    const userVisits = parseInt(localStorage.getItem(ANIMATION_VIEW_COUNT_KEY) || '0', 10);
    if (userVisits >= ANIMATION_THRESHOLD) return;
    localStorage.setItem(ANIMATION_VIEW_COUNT_KEY, String(userVisits + 1));

    hasAnimatedRef.current = true;
    const filters = listRef.current;
    if (!filters) return;
    const animationDelayTimer = setTimeout(
      () =>
        startSlideAnimation({
          filters,
          originalScroll: filters.scrollLeft,
          slideDistance: ANIMATION_SHAKE_OFFSET,
          slideTimes: ANIMATION_SHAKE_CYCLES,
          slideDuration: ANIMATION_DURATION,
        }),
      ANIMATION_DELAY
    );

    return () => clearTimeout(animationDelayTimer);
  }, [isObserved, listRef]);
};
